/**
 * ErrorBoundary.tsx
 *
 * Catches unhandled React errors anywhere in the subtree so a single
 * bad render doesn't blank the whole app. Replaces the broken subtree
 * with a recovery card that lets the user navigate back to the cycles
 * list or refresh the page.
 *
 * Use as the OUTERMOST wrapper inside <App>. Optionally drop one near
 * a risky leaf (e.g. magic-link form) so a sibling crash doesn't take
 * down the whole route.
 */

import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional override for the fallback UI. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-stone-50">
        <div className="max-w-md w-full text-center border rounded-2xl p-8 bg-red-50 border-red-200 text-red-900">
          <span
            className="material-symbols-rounded text-red-600"
            style={{ fontSize: 40 }}
            aria-hidden="true"
          >
            error
          </span>
          <h2 className="text-xl font-semibold tracking-tight mt-3 mb-2">
            Something went wrong
          </h2>
          <p className="text-base leading-relaxed mb-4">
            The page hit an unexpected error and couldn't finish loading.
          </p>
          <details className="text-left text-xs bg-white border border-red-200 rounded-lg p-2 mb-4 max-h-32 overflow-auto">
            <summary className="cursor-pointer text-red-700">Technical details</summary>
            <pre className="mt-2 whitespace-pre-wrap text-stone-700">
              {error.message}
              {error.stack ? '\n\n' + error.stack.split('\n').slice(0, 6).join('\n') : ''}
            </pre>
          </details>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.reset}
              className="px-4 py-2 rounded-lg border border-red-300 bg-white text-red-800 hover:border-red-500 text-sm"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => { window.location.href = '/'; }}
              className="px-4 py-2 rounded-lg bg-red-700 text-white hover:bg-red-800 text-sm"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
